
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Invitation {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'superadmin' | 'member';
  expires_at: string;
}

export const useInvitationValidation = (token: string | undefined) => {
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitation = useCallback(async () => {
    try {
      
      if (!token) {
        setError('No invitation token found in URL. Please check your invitation link.');
        return;
      }

      
      const { data: invitationData, error: queryError } = await supabase
        .from('invitations')
        .select('id, email, first_name, last_name, role, expires_at, status')
        .eq('token', token)
        .single();


      if (queryError) {
        console.error('Database query error:', queryError);
        if (queryError.code === 'PGRST116') {
          setError('Invalid invitation token. Please contact support or request a new invitation.');
        } else {
          setError('Error loading invitation. Please try again.');
        }
        return;
      }

      if (!invitationData) {
        setError('Invalid invitation token. Please contact support or request a new invitation.');
        return;
      }

      if (invitationData.status !== 'pending') {
        if (invitationData.status === 'accepted') {
          setError('This invitation has already been accepted.');
        } else if (invitationData.status === 'expired') {
          setError('This invitation has expired.');
        } else {
          setError(`Invitation status: ${invitationData.status}`);
        }
        return;
      }

      const expiresAt = new Date(invitationData.expires_at);
      const now = new Date();

      if (expiresAt < now) {
        setError('This invitation has expired');
        return;
      }

      setInvitation(invitationData);
    } catch (err) {
      console.error('Unexpected error in fetchInvitation:', err);
      setError(`Unexpected error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchInvitation();
    } else {
      setError('No invitation token found in URL. Please check your invitation link.');
      setLoading(false);
    }
  }, [token, fetchInvitation]);


  return { invitation, loading, error };
};
